// /client/src/pages/Sitesmap.js
//creates the divesite map and loads the details in a modal when the name is clicked

import React, { useEffect, useState } from "react";
import {
  Container,
  Box,
  Typography,
  Stack,
  Paper,
  Modal,
  Button,
  Alert,
  CircularProgress,
} from "@mui/material";
import { useAuth } from "../contexts/AuthContext"; // Import the auth context
import Rating from "@mui/material/Rating";
import { styled } from "@mui/material/styles";
import PropTypes from "prop-types";
import SentimentVeryDissatisfiedIcon from "@mui/icons-material/SentimentVeryDissatisfied";
import SentimentDissatisfiedIcon from "@mui/icons-material/SentimentDissatisfied";
import SentimentSatisfiedIcon from "@mui/icons-material/SentimentSatisfied";
import SentimentSatisfiedAltIcon from "@mui/icons-material/SentimentSatisfiedAltOutlined";
import SentimentVerySatisfiedIcon from "@mui/icons-material/SentimentVerySatisfied";
import round from "lodash/round";

import DivesiteMap from "../maps/divesitemap";
import DivesiteDetails from "../components/divesites/divesitedetails";
import TideChart from "../components/weather/tidegraph";

const StyledRating = styled(Rating)(({ theme }) => ({
  "& .MuiRating-iconEmpty .MuiSvgIcon-root": {
    color: theme.palette.action.disabled,
  },
}));

const customIcons = {
  0: { icon: <SentimentSatisfiedIcon color="action" />, label: "Not Rated" },
  1: {
    icon: <SentimentVeryDissatisfiedIcon color="error" />,
    label: "Very Dissatisfied",
  },
  2: {
    icon: <SentimentDissatisfiedIcon color="error" />,
    label: "Dissatisfied",
  },
  3: { icon: <SentimentSatisfiedIcon color="warning" />, label: "Neutral" },
  4: {
    icon: <SentimentSatisfiedAltIcon color="success" />,
    label: "Satisfied",
  },
  5: {
    icon: <SentimentVerySatisfiedIcon color="success" />,
    label: "Very Satisfied",
  },
};

function IconContainer(props) {
  const { value, ...other } = props;
  return <span {...other}>{customIcons[value].icon}</span>;
}

IconContainer.propTypes = {
  value: PropTypes.number.isRequired,
};

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90%",
  maxWidth: 800,
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
  overflowY: "auto",
  maxHeight: "90vh",
};

const Sitemap = () => {
  const [divesiteheaders, setDivesiteHeaders] = useState(null);
  const [selectedSiteDetails, setSelectedSiteDetails] = useState(null); // For the side panel
  const [selectedSiteForModal, setSelectedSiteForModal] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  // Use the auth context
  const { currentUser, userVerified, logout } = useAuth();

  useEffect(() => {
    if (currentUser) {
      // User is signed in, fetch data
      fetchDivesites();
    } else {
      // User is signed out
      setLoading(false);
      setError("You must be logged in to view dive sites");
    }
  }, [currentUser]);

  const fetchDivesites = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!currentUser) {
        throw new Error("Not authenticated");
      }

      // Get a fresh token
      const token = await currentUser.getIdToken(true);

      const payloadHeader = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };

      console.log(
        "Making API request with token:",
        token.substring(0, 10) + "..."
      );

      const response = await fetch(
        "/api/divesites/",
        payloadHeader
      );

      if (!response.ok) {
        if (response.status === 401) {
          console.log(
            "Authentication failed. Refreshing token and trying again"
          );

          // Try once more with forced token refresh
          const freshToken = await currentUser.getIdToken(true);

          const retryResponse = await fetch(
            "/api/divesites/",
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${freshToken}`,
              },
            }
          );

          if (!retryResponse.ok) {
            const errorText = await retryResponse.text();
            throw new Error(
              `Authentication failed after token refresh: ${errorText}`
            );
          }

          const json = await retryResponse.json();
          setDivesiteHeaders(json);
        } else {
          // Handle other HTTP error statuses
          const errorText = await response.text();
          throw new Error(
            `Server responded with ${response.status}: ${errorText}`
          );
        }
      } else {
        const json = await response.json();
        setDivesiteHeaders(json);
      }

      setLoading(false);
    } catch (e) {
      console.error("Error fetching dive sites:", e);

      if (
        e.message.includes("Firebase ID token has expired") ||
        e.message.includes("Decoding Firebase ID token failed")
      ) {
        setError("Your session has expired. Please log out and log back in.");
      } else {
        setError(`Failed to load dive sites: ${e.message}`);
      }

      setLoading(false);
    }
  };

  const handleMarkerClick = (site) => {
    setSelectedSiteDetails(site); // Keep this for the side panel
  };

  const handleDivesiteNameClick = (site) => {
    setSelectedSiteForModal(site);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedSiteForModal(null);
  };

  const handleRetry = () => {
    if (currentUser) {
      fetchDivesites();
    } else {
      setError("You must be logged in to view dive sites");
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      // Redirect will happen automatically via your auth state listener
    } catch (error) {
      console.error("Error logging out:", error);
    }
  };

  return (
    <Container
      sx={{
        width: "90%",
        maxWidth: 1200,
        borderRadius: 2,
        bgcolor: "primary.main",
        padding: 4,
        margin: "auto",
        marginTop: 3,
      }}
    >
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <>
              <Button
                variant="outlined"
                size="small"
                sx={{ mr: 1 }}
                onClick={handleRetry}
              >
                Retry
              </Button>
              {error.includes("session has expired") && (
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={handleLogout}
                >
                  Log Out
                </Button>
              )}
            </>
          }
        >
          {error}
        </Alert>
      )}

      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "60vh",
          }}
        >
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Loading dive sites...</Typography>
        </Box>
      ) : (
        <Stack direction={{ xs: "column", md: "row" }} spacing={3}>
          <Box
            sx={{ flex: 1, height: 600, borderRadius: 2, overflow: "hidden" }}
          >
            {divesiteheaders && (
              <DivesiteMap
                key="divesite-map"
                sites={divesiteheaders}
                onMarkerClick={handleMarkerClick}
                onNameClick={handleDivesiteNameClick} // Pass the new handler
              />
            )}
          </Box>
          {selectedSiteDetails && (
            <Paper
              sx={{
                flex: 1,
                p: 3,
                bgcolor: "background.paper",
                borderRadius: 2,
                overflowY: "auto",
              }}
            >
              <Stack direction="column" spacing={2}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography variant="h5" gutterBottom>
                    {selectedSiteDetails?.uddf?.divesite?.site?.name}
                  </Typography>
                  <StyledRating
                    name="overallRating"
                    value={
                      selectedSiteDetails?.metadata?.ratings?.avgDiveRating ?? 0
                    }
                    size="small"
                    readOnly
                    highlightSelectedOnly
                    sx={{ ml: 1 }}
                  />
                </Box>
                <Typography variant="body1">
                  {selectedSiteDetails?.metadata?.oneliner ??
                    "Summary not available"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Latitude:{" "}
                  {round(
                    selectedSiteDetails?.uddf?.divesite?.site?.geography
                      ?.latitude,
                    4
                  )}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Longitude:{" "}
                  {round(
                    selectedSiteDetails?.uddf?.divesite?.site?.geography
                      ?.longitude,
                    4
                  )}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Last Viz:
                  <Rating
                    name="vizRating"
                    value={
                      selectedSiteDetails?.metadata?.ratings?.lastviz?.viz ?? 0
                    }
                    size="small"
                    readOnly
                  />
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Slog to Water:
                  <StyledRating
                    name="slogRating"
                    value={selectedSiteDetails?.metadata?.ratings?.slog ?? 0}
                    size="small"
                    readOnly
                    IconContainerComponent={IconContainer}
                    getLabelText={(value) => customIcons[value].label}
                    highlightSelectedOnly
                  />
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Surface Swim:{" "}
                  {selectedSiteDetails?.metadata?.additionalInfo
                    ?.surfaceSwimDistance ?? "N/A"}
                </Typography>
                <Box sx={{ width: "90%", maxWidth: "400px", margin: "0" }}>
                  <TideChart
                    station={
                      selectedSiteDetails?.metadata?.additionalInfo
                        ?.closestTideStation ?? "Tide Data not available"
                    }
                    width={400}
                    height={200}
                  />
                </Box>
              </Stack>
            </Paper>
          )}
        </Stack>
      )}

      <Modal
        open={openModal}
        onClose={handleCloseModal}
        aria-labelledby="divesite-details-modal"
        aria-describedby="divesite-details-description"
      >
        <Box sx={modalStyle}>
          {selectedSiteForModal && (
            <DivesiteDetails divesite={selectedSiteForModal} />
          )}
          <Button onClick={handleCloseModal} sx={{ mt: 2 }}>
            Close
          </Button>
        </Box>
      </Modal>
    </Container>
  );
};

export default Sitemap;
